import React, { useState, useEffect } from 'react';
import { topicApi } from '../services/api.ts';

interface Rater {
  userId: string;
  submittedAt: string;
  usefulnessRating: number;
  recommendationRating: number;
  customerConversations: number;
  hoursSaved: number;
}

interface TopicRatersModalProps {
  isOpen: boolean;
  onClose: () => void;
  topicId: string | null;
  topicTitle: string;
}

const TopicRatersModal: React.FC<TopicRatersModalProps> = ({
  isOpen,
  onClose,
  topicId,
  topicTitle
}) => {
  const [raters, setRaters] = useState<Rater[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && topicId) {
      loadRaters();
    }
  }, [isOpen, topicId]);

  const loadRaters = async () => {
    if (!topicId) return;
    
    try {
      setLoading(true);
      setError('');
      const response = await topicApi.getTopicRaters(topicId);
      setRaters(response.raters || []);
    } catch (error) {
      console.error('Error loading raters:', error);
      setError('Failed to load ratings data');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '700px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: '600' }}>
              Unique Colleague Ratings: {raters.length}
            </h2>
            <p style={{ margin: 0, color: '#1e293b', fontSize: '16px' }}>
              <span style={{ fontWeight: '400', color: '#64748b' }}>Topic:</span> <span style={{ fontWeight: '600' }}>{topicTitle}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            Loading ratings...
          </div>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#dc2626' }}>
            {error}
          </div>
        )}

        {!loading && !error && raters.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            No ratings have been submitted for this topic yet.
          </div>
        )}

        {!loading && !error && raters.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: '600' }}>Colleague</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: '600' }}>Confidence Impact</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: '600' }}>Recommendation</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: '600' }}>Customers Impacted</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: '600' }}>Prep Hours Saved</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: '600' }}>Feedback Provided</th>
                </tr>
              </thead>
              <tbody>
                {raters.map((rater) => (
                  <tr key={rater.userId} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px 8px' }}>
                      <a
                        href={`https://phonetool.amazon.com/users/${rater.userId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#2563eb',
                          textDecoration: 'none',
                          fontWeight: '500'
                        }}
                      >
                        {rater.userId}
                      </a>
                    </td>
                    <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                      {rater.usefulnessRating ? `${rater.usefulnessRating}/5 ⭐` : '-'}
                    </td>
                    <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                      {rater.recommendationRating ? `${rater.recommendationRating}/5 ⭐` : '-'}
                    </td>
                    <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                      {rater.customerConversations || '-'}
                    </td>
                    <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                      {rater.hoursSaved || '-'}
                    </td>
                    <td style={{ textAlign: 'center', padding: '12px 8px', fontSize: '12px', color: '#64748b' }}>
                      {new Date(rater.submittedAt).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopicRatersModal;
